// ISBN auto-fill: Google Books → Open Library fallback.
// Pure client-side fetch; both APIs are CORS-friendly and free for read access.

export type IsbnMetadata = {
  title: string;
  author: string;
  isbn: string;
  description?: string;
  category?: string;
  cover_url?: string;
  publisher?: string;
  published_date?: string;
  language?: string;
  page_count?: number;
};

const ISBN10 = /^[0-9]{9}[0-9X]$/;
const ISBN13 = /^[0-9]{13}$/;

export function normalizeIsbn(raw: string): string | null {
  const cleaned = raw.replace(/[-\s]/g, "").toUpperCase();
  if (ISBN10.test(cleaned) || ISBN13.test(cleaned)) return cleaned;
  return null;
}

async function fromGoogle(isbn: string): Promise<IsbnMetadata | null> {
  try {
    const res = await fetch(
      `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&maxResults=1`,
    );
    if (!res.ok) return null;
    const json: any = await res.json();
    const item = json?.items?.[0]?.volumeInfo;
    if (!item?.title) return null;
    const cover =
      item.imageLinks?.extraLarge ||
      item.imageLinks?.large ||
      item.imageLinks?.medium ||
      item.imageLinks?.thumbnail ||
      item.imageLinks?.smallThumbnail;
    return {
      title: String(item.title),
      author: Array.isArray(item.authors) ? item.authors.join(", ") : (item.authors ?? "Unknown"),
      isbn,
      description: item.description,
      category: Array.isArray(item.categories) ? item.categories[0] : undefined,
      cover_url: cover ? String(cover).replace(/^http:/, "https:") : undefined,
      publisher: item.publisher,
      published_date: item.publishedDate,
      language: item.language,
      page_count: item.pageCount,
    };
  } catch {
    return null;
  }
}

async function fromOpenLibrary(isbn: string): Promise<IsbnMetadata | null> {
  try {
    const res = await fetch(
      `https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`,
    );
    if (!res.ok) return null;
    const json: any = await res.json();
    const item = json?.[`ISBN:${isbn}`];
    if (!item?.title) return null;
    return {
      title: String(item.title),
      author: Array.isArray(item.authors) && item.authors.length
        ? item.authors.map((a: any) => a.name).join(", ")
        : "Unknown",
      isbn,
      description: typeof item.notes === "string" ? item.notes : item.notes?.value,
      category: Array.isArray(item.subjects) && item.subjects[0]?.name
        ? item.subjects[0].name
        : undefined,
      cover_url: item.cover?.large || item.cover?.medium || item.cover?.small,
      publisher: Array.isArray(item.publishers) ? item.publishers[0]?.name : undefined,
      published_date: item.publish_date,
      page_count: item.number_of_pages,
    };
  } catch {
    return null;
  }
}

export async function lookupIsbn(raw: string): Promise<
  | { ok: true; data: IsbnMetadata }
  | { ok: false; reason: "invalid" | "not_found" }
> {
  const isbn = normalizeIsbn(raw);
  if (!isbn) return { ok: false, reason: "invalid" };
  const g = await fromGoogle(isbn);
  if (g) return { ok: true, data: g };
  const o = await fromOpenLibrary(isbn);
  if (o) return { ok: true, data: o };
  return { ok: false, reason: "not_found" };
}
